import { css } from 'aphrodite';
import { FC } from 'react';
import { ConfirmationDialog, IConfirmationDialogOption } from '../../../../web/components/ConfirmationDialog';
import { IModalProps } from '../../../../web/components/Modal';
import { bs } from '../../../../web/styles/styles';
import { StylePreset } from './presets';
import { Column, Row } from '../../../components/Layout';
import { styleSheet as blogStyles } from '../../../components/blogs/styles';

interface PresetPreviewModalProps {
	preset: StylePreset;
	modalProps: IModalProps<boolean>;
	hasUnsavedChanges?: boolean;
}

export const PresetPreviewModal: FC<PresetPreviewModalProps> = ({ preset, modalProps, hasUnsavedChanges }) => {
	const options: IConfirmationDialogOption<boolean>[] = [
		{
			isCta: true,
			representedObject: true,
			title: 'Apply Preset',
		},
		{
			isCancel: true,
			representedObject: false,
			title: 'Cancel',
		},
	];

	return (
		<ConfirmationDialog modalProps={modalProps} options={options} title={`Apply ${preset.name} Preset`}>
			<Column className={css(bs.gap4, bs.pt4)}>
				{hasUnsavedChanges && (
					<div className={css(bs.p4, bs.bgGray200, bs.rounded, bs.mb2)}>
						<strong>Warning:</strong> You have unsaved changes. Applying this preset will replace your current settings.
						Make sure to save your work first if you want to keep it.
					</div>
				)}

				<div className={css(blogStyles.gap8)}>
					<p className={css(bs.textGray600, bs.mb4)}>{preset.description}</p>
				</div>

				{/* Color Preview Section */}
				<div className={css(blogStyles.gap12)}>
					<h3 className={css(blogStyles.subtitleText, bs.mb2)}>Colors</h3>

					{preset.colors.header && (
						<Row className={css(bs.gap4, bs.mb3)}>
							<Column className={css(bs.flex1)}>
								<div className={css(bs.textSm, bs.mb2)}>Header</div>
								<Row className={css(bs.gap2, bs.flexWrap)}>
									{preset.colors.header.backgroundColor && (
										<div className={css(bs.flex, bs.itemsCenter, bs.gap1)}>
											<div
												className={css(bs.w6, bs.h6, bs.rounded, bs.border)}
												style={{ backgroundColor: preset.colors.header.backgroundColor }}
											/>
											<span className={css(bs.textXs, bs.textGray600)}>Background</span>
										</div>
									)}
									{preset.colors.header.textColor && (
										<div className={css(bs.flex, bs.itemsCenter, bs.gap1)}>
											<div
												className={css(bs.w6, bs.h6, bs.rounded, bs.border)}
												style={{ backgroundColor: preset.colors.header.textColor }}
											/>
											<span className={css(bs.textXs, bs.textGray600)}>Text</span>
										</div>
									)}
									{preset.colors.header.hoverColor && (
										<div className={css(bs.flex, bs.itemsCenter, bs.gap1)}>
											<div
												className={css(bs.w6, bs.h6, bs.rounded, bs.border)}
												style={{ backgroundColor: preset.colors.header.hoverColor }}
											/>
											<span className={css(bs.textXs, bs.textGray600)}>Hover</span>
										</div>
									)}
								</Row>
							</Column>
						</Row>
					)}

					{preset.colors.footer && (
						<Row className={css(bs.gap4, bs.mb3)}>
							<Column className={css(bs.flex1)}>
								<div className={css(bs.textSm, bs.mb2)}>Footer</div>
								<Row className={css(bs.gap2, bs.flexWrap)}>
									{preset.colors.footer.backgroundColor && (
										<div className={css(bs.flex, bs.itemsCenter, bs.gap1)}>
											<div
												className={css(bs.w6, bs.h6, bs.rounded, bs.border)}
												style={{ backgroundColor: preset.colors.footer.backgroundColor }}
											/>
											<span className={css(bs.textXs, bs.textGray600)}>Background</span>
										</div>
									)}
									{preset.colors.footer.textColor && (
										<div className={css(bs.flex, bs.itemsCenter, bs.gap1)}>
											<div
												className={css(bs.w6, bs.h6, bs.rounded, bs.border)}
												style={{ backgroundColor: preset.colors.footer.textColor }}
											/>
											<span className={css(bs.textXs, bs.textGray600)}>Text</span>
										</div>
									)}
									{preset.colors.footer.linkColor && (
										<div className={css(bs.flex, bs.itemsCenter, bs.gap1)}>
											<div
												className={css(bs.w6, bs.h6, bs.rounded, bs.border)}
												style={{ backgroundColor: preset.colors.footer.linkColor }}
											/>
											<span className={css(bs.textXs, bs.textGray600)}>Links</span>
										</div>
									)}
								</Row>
							</Column>
						</Row>
					)}
				</div>

				{/* Font Preview Section */}
				<div className={css(blogStyles.gap12, bs.mt4)}>
					<h3 className={css(blogStyles.subtitleText, bs.mb2)}>Fonts</h3>

					{preset.fonts.header && (
						<div className={css(bs.mb2)}>
							<span className={css(bs.textSm)}>Header: </span>
							<span className={css(bs.textSm, bs.textGray600)}>{preset.fonts.header.fontName}</span>
						</div>
					)}

					{preset.fonts.footer && (
						<div className={css(bs.mb2)}>
							<span className={css(bs.textSm)}>Footer: </span>
							<span className={css(bs.textSm, bs.textGray600)}>{preset.fonts.footer?.fontName}</span>
						</div>
					)}
				</div>
			</Column>
		</ConfirmationDialog>
	);
};
